import { fetchWithAutoRefresh } from './fetchWithAutoRefresh';
import { getCsrfToken } from './auth';

const SEND_UPDATE_TIMEOUT_MS = 30_000;
const POLL_REQUEST_TIMEOUT_MS = 15_000;

const fetchWithTimeout = async (url, options = {}, timeoutMs) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      const e = new Error(`Request to ${url} timed out after ${Math.round(timeoutMs / 1000)}s`);
      e.code = 'TIMEOUT';
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};

export async function updateDevkit({ host, app, devkitName, updateBody, onStep, onProgress }) {
  let res;
  try {
    res = await fetchWithTimeout(
      `/api/send-update`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCsrfToken()
        },
        body: JSON.stringify({ updateBody, host, app }),
        credentials: 'include'
      },
      SEND_UPDATE_TIMEOUT_MS
    );
  } catch (err) {
    if (err?.code === 'TIMEOUT') {
      throw new Error(
        `Couldn't reach the dashboard server within ${Math.round(SEND_UPDATE_TIMEOUT_MS / 1000)}s. Check your network connection and try again.`
      );
    }
    throw new Error(`send-update request error: ${err?.message || err}`);
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`send-update failed: ${res.status} ${text}`);
  }

  // Poseidon resolves "latest" itself and echoes the concrete version/build in its ack.
  const ack = await res.json().catch(() => ({}));
  const expected = ack?.version && ack.version !== 'latest' ? { version: ack.version, build: ack.build } : updateBody;

  // A Poseidon self-update restarts the service we'd poll (status 404s mid-restart),
  // so the ack is the confirmation; the card refreshes versions afterwards.
  if (app === 'poseidon') return ack;

  // Best-effort realtime stream; polling is the source of truth.
  const progressWs = openProgressSocket(host, onProgress);

  try {
    await waitForUpdateCompletion(host, app, devkitName, onStep, onProgress, expected);
  } finally {
    if (progressWs) {
      try { progressWs.close(); } catch { /* */ }
    }
  }
}

const openProgressSocket = (host, onProgress) => {
  if (typeof window === 'undefined' || typeof WebSocket === 'undefined') return null;
  try {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${window.location.host}/api/update-progress?host=${encodeURIComponent(host)}`;
    const ws = new WebSocket(url);
    ws.onmessage = (ev) => {
      if (!onProgress) return;
      try {
        const msg = JSON.parse(ev.data);
        if (msg && msg.type === 'progress') onProgress(msg);
      } catch { /* */ }
    };
    return ws;
  } catch {
    return null;
  }
};

// Returns a promise that resolves when update completes or rejects with reason
const waitForUpdateCompletion = (ip, app, devkitName, onStep, onProgress, updateBody) => {
  return new Promise((resolve, reject) => {
    const key = `${app}_update_in_progress`;
    let pollAttempts = 0;
    const intervalMs = 3000; // 3 seconds
    const maxPollAttempts = 400; // 20 minutes / 3s interval = 400 attempts
    let interval;

    // Helper function to get current version from status data
    const getCurrentVersion = (data, app) => {
      switch (app) {
        case 'ironman':
          return {
            version: data.ironman?.ironman_version,
            build: data.ironman?.ironman_build_number
          };
        case 'poseidon':
          return {
            version: data.poseidon?.poseidon_version,
            build: null // poseidon doesn't seem to have build numbers
          };
        case 'proteuscore':
          return {
            version: data.proteuscore?.proteuscore_version,
            build: data.proteuscore?.proteuscore_build_number
          };
        case 'apollocore':
          return {
            version: data.apollocore?.apollocore_version,
            build: data.apollocore?.apollocore_build_number
          };
        default:
          return { version: null, build: null };
      }
    };

    // Helper function to check if update was successful
    const isUpdateSuccessful = (currentVersion, expectedVersion) => {
      // Convert to strings for comparison to handle number/string mismatches
      const currentVersionStr = String(currentVersion.version ?? '').trim();
      const expectedVersionStr = String(expectedVersion.version ?? '').trim();
      const currentBuildStr = String(currentVersion.build ?? '').trim();
      const expectedBuildStr = String(expectedVersion.build ?? '').trim();

      const versionMatch = currentVersionStr === expectedVersionStr;

      // Only compare builds when both sides report one (Poseidon status has no build number)
      const buildMatch = !expectedBuildStr || !currentBuildStr || currentBuildStr === expectedBuildStr;

      return versionMatch && buildMatch;
    };

    const finishWithResult = (ok, message) => {
      if (onStep) onStep(message || '');
      setTimeout(() => { if (onStep) onStep(''); }, ok ? 3000 : 15000);
      (ok ? resolve : reject)(ok ? undefined : new Error(message || 'Update failed'));
    };

    let noUpdateSince = null; // tracks when "no update in progress" started
    let consecutivePollFailures = 0;
    let sawUpdating = false;

    const pollStatus = async () => {
        pollAttempts++;
        try {
            const res = await fetchWithTimeout(
              `/api/device-status/${ip}`,
              {
                credentials: 'include',
                headers: { 'X-CSRF-TOKEN': getCsrfToken() }
              },
              POLL_REQUEST_TIMEOUT_MS
            );

            if (res.status === 404) {
            clearInterval(interval);
            const msg = `Device ${devkitName || ip} not reachable (404)`;
            finishWithResult(false, msg);
            return;
            }

            if (!res.ok) {
            if (pollAttempts >= maxPollAttempts) {
                clearInterval(interval);
                const msg = `Device ${devkitName || ip} not reachable`;
                finishWithResult(false, msg);
            }
            return;
            }

            consecutivePollFailures = 0;
            const data = await res.json();
            const updating = data.update_status?.[key];
            const step = data.update_status?.update_step;
            const progress = data.update_status?.update_progress ?? null;

            if (updating) sawUpdating = true;

            // Suppress "No update in progress" text after completion
            const finalizingIdle = !updating && sawUpdating;

            if (onProgress) onProgress(progress);
            if (onStep) {
              onStep(finalizingIdle ? 'Finalizing update…' : (progress?.message || step || ''));
            }

            if (progress?.error) {
              clearInterval(interval);
              finishWithResult(false, `Update failed: ${progress.error}`);
              return;
            }

            if (!updating) {
                const currentVersion = getCurrentVersion(data, app);
                const expectedVersion = updateBody;

                // Check if update was started and compare version numbers
                if (sawUpdating && isUpdateSuccessful(currentVersion, expectedVersion)) {
                    clearInterval(interval);
                    finishWithResult(true, 'Update completed successfully');
                    return;
                }

                if (!noUpdateSince) {
                    noUpdateSince = Date.now(); // first time we see "no update"
                }

                // 3s buffer if never saw updating=true and the post-update verification didn't match
                if (Date.now() - noUpdateSince > 3000) {
                    clearInterval(interval);

                    if (isUpdateSuccessful(currentVersion, expectedVersion)) {
                        finishWithResult(true, 'Update completed successfully');
                    } else {
                        const msg = `Update failed: Expected ${app} version ${expectedVersion.version}` +
                            `${expectedVersion.build ? ` (build ${expectedVersion.build})` : ''}, but got version ${currentVersion.version}` +
                            `${currentVersion.build ? ` (build ${currentVersion.build})` : ''}`;
                        finishWithResult(false, msg);
                    }
                }
            } else {
                // reset if update is back in progress
                noUpdateSince = null;
            }

            // Timeout handling
            if (pollAttempts >= maxPollAttempts) {
            clearInterval(interval);
            finishWithResult(false, 'Update timed out while in progress');
            }
        } catch (err) {
            consecutivePollFailures += 1;
            if (consecutivePollFailures >= 2 && onStep) {
              onStep(
                err?.code === 'TIMEOUT'
                  ? 'Waiting for device to respond — network may be slow…'
                  : `Connection issue, retrying… (${err?.message || 'unknown error'})`
              );
            }
            if (pollAttempts >= maxPollAttempts) {
            clearInterval(interval);
            const msg = `Polling error for ${devkitName || ip}: ${err.message}`;
            finishWithResult(false, msg);
            }
        }
    };

    // Give the device a moment to register the update before first check
    setTimeout(() => {
      pollStatus();
      // Then continue polling every few seconds
      interval = setInterval(pollStatus, intervalMs);
    }, 2000); // 2 second initial delay
  });
};